import React from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sparkles, FileText, Zap, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const AIToolsPage = () => {
  const navigate = useNavigate();

  const tools = [
    {
      id: 'job-match',
      icon: Sparkles,
      title: 'Job Match',
      description: 'AI-powered job recommendations based on your profile and preferences',
      color: 'from-[#1a5c0a] to-[#3f8033]',
      comingSoon: false
    },
    {
      id: 'resume-builder',
      icon: FileText,
      title: 'Resume Builder',
      description: 'Create an AI-optimized resume that gets noticed by employers',
      color: 'from-[#1a5c0a] to-[#3f8033]',
      comingSoon: false
    },
    {
      id: 'orion-copilot',
      icon: Zap,
      title: 'Orion Copilot',
      description: 'Your AI assistant for career advice and application support',
      color: 'from-orange-500 to-red-500',
      comingSoon: false
    },
    {
      id: 'interview-prep',
      icon: MessageSquare,
      title: 'Interview Prep',
      description: 'Practice with AI-powered mock interviews and feedback',
      color: 'from-green-500 to-teal-500',
      comingSoon: true
    }
  ];

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
          <div className="mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">AI Tools</h1>
            <p className="text-lg sm:text-xl text-gray-600">
              Leverage AI to enhance your job search and career growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {tools.map((tool) => {
              const Icon = tool.icon;
              return (
                <Card key={tool.id} className="p-8 hover:shadow-lg transition-shadow relative overflow-hidden">
                  {tool.comingSoon && (
                    <div className="absolute top-4 right-4">
                      <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-3 py-1 rounded-full">
                        Coming Soon
                      </span>
                    </div>
                  )}
                  
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center mb-6`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{tool.title}</h3>
                  <p className="text-gray-600 mb-6">{tool.description}</p>

                  <Button
                    className="w-full"
                    disabled={tool.comingSoon}
                    onClick={() => {
                      if (tool.id === 'job-match') navigate('/explore');
                      if (tool.id === 'resume-builder') navigate('/profile');
                      if (tool.id === 'orion-copilot') alert('Orion Copilot will be available soon!');
                    }}
                  >
                    {tool.comingSoon ? 'Coming Soon' : 'Open Tool'}
                  </Button>
                </Card>
              );
            })}
          </div>

          {/* Features Section */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">How AI Tools Help You</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="p-6 text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Smart Matching</h3>
                <p className="text-sm text-gray-600">
                  Get personalized job recommendations that truly fit your skills and goals
                </p>
              </Card>

              <Card className="p-6 text-center">
                <div className="w-12 h-12 bg-[#E0E8D0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-[#0A3900]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Optimized Profile</h3>
                <p className="text-sm text-gray-600">
                  AI-enhanced resume and profile that stands out to employers
                </p>
              </Card>

              <Card className="p-6 text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">24/7 Support</h3>
                <p className="text-sm text-gray-600">
                  Get instant answers and guidance from your AI career assistant
                </p>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIToolsPage;